import React from "react";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  ActionLink,
  BannerSeverity,
  ExperienceCancellationPolicy,
  Icon,
  IconName,
  NotificationBanner,
  onEnter,
} from "halifax";
import {
  CardPaymentSelectors,
  ContactSelectors,
  RewardsPaymentSelectors,
  useCheckoutState,
  useCheckoutStateSelector as useSelector,
} from "@capone/checkout";
import { RouteComponentProps } from "react-router-dom";

import "./styles.scss";
import { getConfigTenant, isVesperTenant } from "@capone/common";
import {
  ACTIVITY_REVIEW_HEADING_TEXT,
  ACTIVITY_REVIEW_SUBTITLE_TEXT,
  CONFIRMATION_EMAIL_HEADING,
  VOUCHER_EMAIL_NOTICE,
} from "./textConstants";
import {
  TravelerSelection,
  ContactInfo,
  PriceBreakdown,
  ActivitySummaryPanel,
} from "..";
import { ActivitySelectors } from "../../state/selectors";
import {
  ADDITIONAL_DETAILS,
  CANCELLATION_POLICY,
  CHANGE_CTA_TEXT,
} from "../../../common/textConstants";
import { ActivityAdditionalDetailsSelectors } from "../../../../checkout";
import { AdditionalDetailsCompleted } from "../AdditionalDetails";
import { Event, TEvent } from "../../state/events";
import { ActivityMachineContext } from "../../state/types";
import { EarnInfoBanner } from "../../../common";
import { MobileTripReviewConnectorProps } from "./container";
import { ClientContext } from "../../../../App";
import { EXPERIENCES_REWARDS_DISABLED_BANNER_TEXT } from "../../../rewards/components/RewardsBanner/constants";
import {
  AVAILABLE,
  CHECKOUT_DEFAULT_COMM_EXPERIMENT,
  getExperimentVariant,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../../context/experiments";

export interface PriceBreakdownProps
  extends RouteComponentProps,
    MobileTripReviewConnectorProps {}

export const MobileTripReview = ({
  largestValueAccount,
}: PriceBreakdownProps) => {
  const [, send] = useCheckoutState<TEvent, ActivityMachineContext>();

  const { isRewardsDisabled } = React.useContext(ClientContext);

  const expState = useExperiments();
  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;
  const isDefaultCommEnabled =
    getExperimentVariant(expState.experiments, CHECKOUT_DEFAULT_COMM_EXPERIMENT) ===
    AVAILABLE;
  const isVesper = isVesperTenant(getConfigTenant());

  const activityShop = useSelector(ActivitySelectors.getActivityShop);

  const accountEmail = useSelector(ContactSelectors.getEmailAddress);

  const priceBreakdown = useSelector(
    ActivitySelectors.getActivityPriceBreakdown
  );
  const hasCompletedAdditionalDetailsStep = useSelector(
    ActivityAdditionalDetailsSelectors.getHasCompletedAdditionalDetailsStep
  );

  const rewardsAccounts =
    useSelector(RewardsPaymentSelectors.getRewardsAccounts) || [];
  const selectedPaymentMethodRewardsAccountId = useSelector(
    CardPaymentSelectors.getSelectedPaymentMethodRewardsAccountId
  );
  const isCreditCardPaymentRequired = useSelector(
    CardPaymentSelectors.getIsCreditCardPaymentRequired
  );
  const cardPaymentAmountRequired = useSelector(
    CardPaymentSelectors.getTotalCardPaymentRequired
  );

  return (
    <Box className={clsx("activity-checkout-review", { redesigned: isVesper })}>
      <Typography className="mobile-header-title" variant="h3">
        {ACTIVITY_REVIEW_HEADING_TEXT}
      </Typography>
      <Typography className="mobile-header-subtitle">
        {ACTIVITY_REVIEW_SUBTITLE_TEXT}
      </Typography>
      <hr className="heading-rule" />

      {isCreditCardPaymentRequired && (
        <EarnInfoBanner
          largestValueAccount={
            rewardsAccounts.find(
              ({ accountReferenceId }) =>
                accountReferenceId === selectedPaymentMethodRewardsAccountId
            ) || largestValueAccount
          }
          priceBreakdown={priceBreakdown}
          cardPaymentAmountRequired={cardPaymentAmountRequired}
        />
      )}

      <ActivitySummaryPanel displayChangeBtn />
      <TravelerSelection isMobile />
      {isDefaultCommEnabled ? (
        accountEmail && (
          <Box className="mobile-review-confirmation-email-container">
            <Typography className="mobile-review-confirmation-email-title">
              {CONFIRMATION_EMAIL_HEADING}
            </Typography>
            <Typography className="mobile-review-confirmation-email-notice">
              {VOUCHER_EMAIL_NOTICE} {accountEmail}
            </Typography>
          </Box>
        )
      ) : (
        <ContactInfo isMobile />
      )}

      {hasCompletedAdditionalDetailsStep && (
        <Box className="mobile-review-additional-details-container">
          <Typography className="mobile-review-additional-details-title">
            {ADDITIONAL_DETAILS}
          </Typography>
          <AdditionalDetailsCompleted />
          <ActionLink
            className="edit-button"
            content={
              <Typography className="action-link-text">
                {CHANGE_CTA_TEXT}
              </Typography>
            }
            label={CHANGE_CTA_TEXT}
            onClick={() => send(Event.GO_TO_ADDITIONAL_DETAILS)}
            onKeyDown={(e) =>
              onEnter(e, () => send(Event.GO_TO_ADDITIONAL_DETAILS))
            }
          />
        </Box>
      )}

      {activityShop?.cancellationPolicy && (
        <Box className="mobile-review-cancellation-policy-container">
          <Typography className="mobile-review-cancellation-policy-title">
            {CANCELLATION_POLICY}
          </Typography>
          <ExperienceCancellationPolicy
            cancellationPolicy={activityShop.cancellationPolicy}
            selectedDate={priceBreakdown?.date}
            selectedTime={priceBreakdown?.startTime}
            startingTimeZone={activityShop.startingTimeZone}
            isXmodRedesign={isXmodRedesign}
          />
        </Box>
      )}
      <PriceBreakdown isMobile />
      {isRewardsDisabled && (
        <NotificationBanner
          className="rewards-disabled-banner"
          label={EXPERIENCES_REWARDS_DISABLED_BANNER_TEXT}
          severity={BannerSeverity.WARNING}
          icon={<Icon className="banner-icon" name={IconName.WarningAlert} />}
        />
      )}
    </Box>
  );
};
